import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import mixpanel from "mixpanel-browser";
import { toast } from "react-toastify";
import { ValidationError } from "yup";
import ContentContainer from "../../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import ConfirmationModal from "../../../../ui-components/confirmation-modal/confirmationModal";
import XGSCheckbox from "../../../../ui-components/xgs-checkbox/xgsCheckbox";
import LabeledTextInput from "../../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { PAGE_IDS } from "../../../../app/data/common/pageIds";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import {
  FACTORING_NOTIFICATION_EVENTS,
  FactoringNotificationEvent,
  NotificationRecipient,
  NotificationRecipientRequest,
  NotificationRecipientSchema,
} from "../../../../app/data/invoice-factoring/models";
import { FACTORING_QUERY_OPTIONS, NOTIFICATION_EVENT_LABELS, formatDateTime } from "../constants";
import { useFactoringContext } from "../common/useFactoringContext";
import FactoringNav from "../common/factoringNav";
import ActivityList from "../common/activityList";
import "../invoiceFactoring.scss";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

const toggleEvent = (events: FactoringNotificationEvent[], event: FactoringNotificationEvent) =>
  events.includes(event) ? events.filter((value) => value !== event) : [...events, event];

const FactoringSettings: React.FC = () => {
  const queryClient = useQueryClient();
  const { canManage } = useFactoringContext();
  const queryKey = [QUERY_KEYS.INVOICE_FACTORING_NOTIFICATIONS];

  const [newEmail, setNewEmail] = useState("");
  const [newEvents, setNewEvents] = useState<FactoringNotificationEvent[]>([...FACTORING_NOTIFICATION_EVENTS]);
  const [formError, setFormError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<NotificationRecipient | null>(null);

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "invoice_factoring_settings" });
  }, []);

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => invoiceFactoringService.getNotificationSettings(),
    retry: false,
    ...FACTORING_QUERY_OPTIONS,
  });

  const refresh = () => queryClient.invalidateQueries(queryKey);

  const createMutation = useMutation({
    mutationFn: (request: NotificationRecipientRequest) => invoiceFactoringService.createRecipient(request),
    onSuccess: (recipient) => {
      toast.info(`${recipient.email} added.`);
      setNewEmail("");
      setNewEvents([...FACTORING_NOTIFICATION_EVENTS]);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message || "Unable to add the recipient."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<NotificationRecipientRequest> }) =>
      invoiceFactoringService.updateRecipient(id, changes),
    onSuccess: refresh,
    onError: (e: Error) => {
      toast.error(e.message || "Unable to update the recipient.");
      refresh();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => invoiceFactoringService.deleteRecipient(id),
    onSuccess: () => {
      toast.info("Recipient removed.");
      setRemoving(null);
      refresh();
    },
    onError: (e: Error) => toast.error(e.message || "Unable to remove the recipient."),
  });

  const onAdd = async () => {
    const request: NotificationRecipientRequest = { email: newEmail.trim(), events: newEvents, active: true };
    try {
      await NotificationRecipientSchema.validate(request);
    } catch (e) {
      setFormError((e as ValidationError).message);
      return;
    }
    setFormError(null);
    createMutation.mutate(request);
  };

  const onToggleEvent = (recipient: NotificationRecipient, event: FactoringNotificationEvent) => {
    const events = toggleEvent(recipient.events, event);
    if (!events.length) {
      toast.error("Keep at least one notification, or remove the recipient.");
      return;
    }
    updateMutation.mutate({ id: recipient.id, changes: { events } });
  };

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ "full-width": true }}
      pageId={PAGE_IDS.INVOICE_FACTORING_SETTINGS}
      isLoading={isLoading}
      titleComponent={
        <ContentContainerToolbar
          pageId={PAGE_IDS.INVOICE_FACTORING_SETTINGS}
          title="Factoring Notification Settings"
          mods={{ "full-width": true }}
        />
      }
    >
      <div className="xgs-invoice-factoring">
        <FactoringNav />

        {isError && (
          <div className="xgs-invoice-factoring__error-box">
            Unable to load notification settings. {(error as Error)?.message}
          </div>
        )}

        {data && (
          <>
            <p className="xgs-invoice-factoring__muted">
              Recipients get an email when a batch starts, finishes, stops, or fails. Emails for finished batches include
              the results report.
            </p>

            <table className="xgs-invoice-factoring__doc-table xgs-invoice-factoring__doc-table--wide xgs-invoice-factoring__recipients">
              <thead>
                <tr>
                  <th>Email</th>
                  {FACTORING_NOTIFICATION_EVENTS.map((event) => (
                    <th key={event}>{NOTIFICATION_EVENT_LABELS[event]}</th>
                  ))}
                  <th>Active</th>
                  <th>Last change</th>
                  {canManage && <th />}
                </tr>
              </thead>
              <tbody>
                {data.recipients.map((recipient) => (
                  <tr key={recipient.id} className={recipient.active ? undefined : "xgs-invoice-factoring__recipient--inactive"}>
                    <td>{recipient.email}</td>
                    {FACTORING_NOTIFICATION_EVENTS.map((event) => (
                      <td key={event}>
                        {canManage ? (
                          <XGSCheckbox
                            name={`${recipient.id}-${event}`}
                            checked={recipient.events.includes(event)}
                            onChange={() => onToggleEvent(recipient, event)}
                          />
                        ) : recipient.events.includes(event) ? (
                          "Yes"
                        ) : (
                          "No"
                        )}
                      </td>
                    ))}
                    <td>
                      {canManage ? (
                        <XGSCheckbox
                          name={`${recipient.id}-active`}
                          checked={recipient.active}
                          onChange={() =>
                            updateMutation.mutate({ id: recipient.id, changes: { active: !recipient.active } })
                          }
                        />
                      ) : recipient.active ? (
                        "Yes"
                      ) : (
                        "No"
                      )}
                    </td>
                    <td>
                      <div className="xgs-invoice-factoring__two-line">
                        <span>{formatDateTime(recipient.updatedAt || recipient.createdAt)}</span>
                        <span className="xgs-invoice-factoring__muted">{recipient.updatedBy || recipient.createdBy}</span>
                      </div>
                    </td>
                    {canManage && (
                      <td>
                        <Button
                          theme={ButtonThemes.blueInverted}
                          size={ButtonSizes.auto}
                          type="button"
                          onClick={() => setRemoving(recipient)}
                        >
                          Remove
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {!data.recipients.length && (
                  <tr>
                    <td colSpan={FACTORING_NOTIFICATION_EVENTS.length + 3 + (canManage ? 1 : 0)} className="xgs-invoice-factoring__muted">
                      No recipients yet.{canManage ? " Add one below." : ""}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {canManage && (
              <div className="xgs-invoice-factoring__card xgs-invoice-factoring__add-recipient">
                <div className="xgs-invoice-factoring__card-title">Add recipient</div>
                <LabeledTextInput
                  label="Email:"
                  labelMode={LabelModes.column}
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail((e.target as HTMLInputElement).value)}
                  placeholder="name@xgsi.com"
                  className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-search"
                />
                <div className="xgs-invoice-factoring__event-options">
                  {FACTORING_NOTIFICATION_EVENTS.map((event) => (
                    <XGSCheckbox
                      key={event}
                      name={`new-${event}`}
                      checked={newEvents.includes(event)}
                      onChange={() => setNewEvents(toggleEvent(newEvents, event))}
                    >
                      {NOTIFICATION_EVENT_LABELS[event]}
                    </XGSCheckbox>
                  ))}
                </div>
                {formError && <div className="xgs-invoice-factoring__text--error">{formError}</div>}
                <div>
                  <Button
                    theme={ButtonThemes.blue}
                    size={ButtonSizes.auto}
                    type="button"
                    spinner={createMutation.isLoading}
                    disabled={createMutation.isLoading || !newEmail.trim()}
                    onClick={onAdd}
                  >
                    Add recipient
                  </Button>
                </div>
              </div>
            )}

            <h3 className="xgs-invoice-factoring__section-title">Activity</h3>
            <ActivityList entries={data.activity} emptyText="No changes to notification settings yet." />
          </>
        )}
      </div>

      <ConfirmationModal
        opened={!!removing}
        header="Remove Recipient"
        confirmButtonText="Remove"
        spinner={deleteMutation.isLoading}
        fitContent
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && deleteMutation.mutate(removing.id)}
      >
        {removing ? `${removing.email} will stop getting invoice factoring emails.` : null}
      </ConfirmationModal>
    </ContentContainer>
  );
};

export default FactoringSettings;
